import { DropResult } from "@hello-pangea/dnd";
import { useMemo, useState } from "react";
import { usePermissions } from "../../../../hooks/usePermissions";
import type { IncidentDatabase } from "../../../../types";
import { useGetSubTopicIncidents } from "../../api/useGetSubTopicIncidents";
import { useUpdateIncidentStatus } from "../../api/useUpdateIncidentStatus";

type Props = {
  subTopicId: string;
};

export function useIncidentsTab(props: Props) {
  const { isAdmin } = usePermissions();
  const { data: incidents, isLoading, error } = useGetSubTopicIncidents(props.subTopicId);
  const { mutate: updateIncidentStatus, isPending: isUpdating } = useUpdateIncidentStatus();
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedIncidentToEdit, setSelectedIncidentToEdit] = useState<IncidentDatabase | null>(null);

  const { ongoingIncidents, resolvedIncidents } = useMemo(() => {
    if (!incidents) {
      return { ongoingIncidents: [], resolvedIncidents: [] };
    }

    const sortByDateDesc = (a: IncidentDatabase, b: IncidentDatabase) => {
      const timeA = new Date(a.date).getTime();
      const timeB = new Date(b.date).getTime();
      return timeB - timeA;
    };

    const ongoing: IncidentDatabase[] = [];
    const resolved: IncidentDatabase[] = [];

    for (const item of incidents) {
      if (item.status === 'resolved') {
        resolved.push(item);
      } else {

        ongoing.push(item);
      }
    }

    ongoing.sort(sortByDateDesc);
    resolved.sort(sortByDateDesc);

    return { ongoingIncidents: ongoing, resolvedIncidents: resolved };
  }, [incidents]);

  function handleDragEnd(result: DropResult) {
    if (!result.destination) {
      return;
    }

    const { source, destination, draggableId } = result;

    if (source.droppableId === destination.droppableId) {
      return;
    }

    const newStatus = destination.droppableId === 'resolved' ? 'resolved' : 'in_progress';

    updateIncidentStatus({
      incidentId: draggableId,
      subjectId: props.subTopicId,
      status: newStatus,
    });
  }

  function handleStatusChange(incidentId: string, newStatus: 'in_progress' | 'resolved') {
    updateIncidentStatus({
      incidentId,
      subjectId: props.subTopicId,
      status: newStatus,
    });
  }

  function handleOpenNewModal() {
    setIsNewModalOpen(true);
  }

  function handleCloseNewModal() {
    setIsNewModalOpen(false);
  }

  function handleOpenEditModal(incident: IncidentDatabase) {
    setSelectedIncidentToEdit(incident);
  }

  function handleCloseEditModal() {
    setSelectedIncidentToEdit(null);
  }

  return {
    isAdmin,
    ongoingIncidents,
    resolvedIncidents,
    totalCount: (incidents?.length ?? 0),
    isLoading,
    error,
    isUpdating,
    isNewModalOpen,
    selectedIncidentToEdit,
    handleDragEnd,
    handleStatusChange,
    handleOpenNewModal,
    handleCloseNewModal,
    handleOpenEditModal,
    handleCloseEditModal,
  };
}

